import { RMDCalculationResult } from '@ndvr/portfolio-api';

import { getThisYear, parseLocalDate } from 'utils/date-formatter';

export interface RmdOwnerGroup {
    ownerPersonOrgGUID: string;
    firstName: string;
    lastName: string;
    /** Age of the account owner (taken from the first account in the group). */
    age: number | undefined;
    accounts: RMDCalculationResult[];
    totalRmdObligation: number;
    /** Sum of ytdRmdSatisfied across accounts. undefined if no account has data yet. */
    totalYtdSatisfied: number | undefined;
    /** Sum of yearEndNav across accounts. undefined if no account has a prior year balance. */
    totalYearEndNav: number | undefined;
    /** Sum of all planned withdrawal finalAmounts across accounts. */
    totalPlannedWithdrawals: number;
}

export type RmdStatus = 'complete' | 'on-track' | 'at-risk';

export function getRmdStatus(obligation: number, ytd: number | undefined, planned: number): RmdStatus {
    if (ytd === undefined) return 'at-risk';
    if (ytd >= obligation) return 'complete';
    if (ytd + planned >= obligation) return 'on-track';
    return 'at-risk';
}

export function groupResultsByOwner(results: RMDCalculationResult[]): RmdOwnerGroup[] {
    const map = new Map<string, RmdOwnerGroup>();

    for (const result of results) {
        const key = result.ownerPersonOrgGUID ?? result.accountGUID;
        if (!map.has(key)) {
            map.set(key, {
                ownerPersonOrgGUID: key,
                firstName: result.firstName ?? '',
                lastName: result.lastName ?? '',
                age: result.age,
                accounts: [],
                totalRmdObligation: 0,
                totalYtdSatisfied: undefined,
                totalYearEndNav: undefined,
                totalPlannedWithdrawals: 0,
            });
        }
        const owner = map.get(key)!;
        owner.accounts.push(result);
        owner.totalRmdObligation += parseFloat(result.rmdObligation);
        if (result.ytdRmdSatisfied !== undefined) {
            owner.totalYtdSatisfied = (owner.totalYtdSatisfied ?? 0) + parseFloat(result.ytdRmdSatisfied);
        }
        if (result.yearEndNav !== undefined) {
            owner.totalYearEndNav = (owner.totalYearEndNav ?? 0) + parseFloat(result.yearEndNav);
        }
        const taxYear = getThisYear();
        for (const w of result.withdrawals ?? []) {
            if (parseLocalDate(w.withdrawalDate).getFullYear() === taxYear) {
                owner.totalPlannedWithdrawals += parseFloat(w.finalAmount);
            }
        }
    }

    return Array.from(map.values());
}
